import React, { createContext, useContext, useEffect, useState } from 'react';
import Cookies from 'universal-cookie';

const cookies = new Cookies();

interface AuthContextType {
  username: string;
  first_name: string;
  last_name: string;
  email: string;
  organization: string;
  organization_id: number;
  ps_id: number;
  plan: string;
  organization_permissions: string[];
  isAuthenticated: boolean;
  department: string; // ✅ NEW
  is_staff: boolean;
  coverage_area: string;
  error: string;
  getPermissions: () => Promise<{ groups: Record<string, any> }>;
  getDashboardPermissions: () => Promise<{ groups: Record<string, any> }>;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  whoami: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [username, setUsername] = useState('');
  const [first_name, setFirstName] = useState('');
  const [last_name, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [organization, setOrganization] = useState('');
  const [organization_id, setOrganization_id] = useState(-1);
  const [ps_id, setPs_id] = useState(-1);
  const [plan, setPlan] = useState('');
  const [organizationPermissions, setOrganizationPermissions] = useState<string[]>([]);
  const [coverage_area, setCoverageArea] = useState('');
  const [error, setError] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [department, setDepartment] = useState('');
  const [is_staff, setIs_staff] = useState(false);

  useEffect(() => {
    getSession();
  }, []);

  // const getSession = async () => {
  //   try {
  //     const res = await fetch('http://localhost:8000/portal/user_session/', {
  //       credentials: 'include',
  //     });
  //     const data = await res.json();
  //     setIsAuthenticated(data.isauthenticated);
  //     setUsername(data.username);
  //     setFirstName(data.first_name);
  //     setLastName(data.last_name);
  //     setEmail(data.email);
  //     setOrganization(data.organization); // ✅
  //     setOrganization_id(data.organization_id); // ✅
  //     setPlan(data.plan); // ✅
  //     setOrganizationPermissions(data.organization_permissions || []); // ✅
  //     setIs_staff(data.is_staff);
  //   } catch (err) {
  //     console.error(err);
  //   }
  // };

  const getSession = async () => {
    try {
      const res = await fetch('/portal/user_session/', {
        credentials: 'same-origin',
      });

      if (!res.ok) throw new Error('Primary session fetch failed');

      const data = await res.json();
      setIsAuthenticated(data.isauthenticated);
      setUsername(data.username);
      setFirstName(data.first_name);
      setLastName(data.last_name);
      setEmail(data.email);
      setOrganization(data.organization); // ✅
      setOrganization_id(data.organization_id); // ✅
      setPs_id(data.ps_id); // ✅
      setPlan(data.plan); // ✅
      setOrganizationPermissions(data.organization_permissions || []); // ✅
      setCoverageArea(data.coverage_area);
      setIs_staff(data.is_staff);
      setDepartment(data.department);
    } catch (err) {
      console.warn('Primary session fetch failed, trying fallback...', err);

      try {
        const fallbackRes = await fetch('/apidet/session', {
          credentials: 'same-origin',
        });

        if (!fallbackRes.ok) throw new Error('Fallback session fetch failed');

        const data = await fallbackRes.json();
        setIsAuthenticated(data.isauthenticated);
        setUsername(data.username);
        setFirstName(data.first_name);
        setLastName(data.last_name);
        // Email, org, etc. might not be available from fallback
      } catch (fallbackErr) {
        console.error('Both session fetch attempts failed:', fallbackErr);
      }
    }
  };

  const getPermissions = async () => {
    try {
      const res = await fetch('/apidet/permissions/', {
        credentials: 'same-origin',
      });
      const data = await res.json();
      setIsAuthenticated(data.isauthenticated);
      return data.groups;
    } catch (err) {
      console.error(err);
    }
  };

  const getDashboardPermissions = async () => {
    try {
      const res = await fetch('/apidet/dashboard_permissions/', {
        credentials: 'same-origin',
      });
      const data = await res.json();
      setIsAuthenticated(data.isauthenticated);
      return data.groups;
    } catch (err) {
      console.error(err);
    }
  };

  const whoami = async () => {
    try {
      const res = await fetch('/apidet/whoami', {
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
      });
      const data = await res.json();
      setUsername(data.username);
    } catch (err) {
      console.error(err);
    }
  };

  const getCsrfToken = (): string | null => {
    const metaTag = document.querySelector('meta[name="csrf-token"]');
    return metaTag ? metaTag.getAttribute('content') : null; // Return null if the meta tag is not found
  };

  // const fetchCsrfToken = async () => {
  //   const response = await fetch('http://localhost:8000/api/csrf-token/', {
  //     method: 'GET',
  //     credentials: 'include', // include cookies in the request
  //   });

  //   if (response.ok) {
  //     const data = await response.json();
  //     return data.csrfToken; // Return the CSRF token
  //   } else {
  //     console.error('Failed to fetch CSRF token');
  //     return null;
  //   }
  // };

  const login = async (username: string, password: string) => {
    try {
      let csrfToken = cookies.get('csrftoken'); // Get the CSRF token from cookies

      if (!csrfToken) {
        csrfToken = getCsrfToken();
        if (!csrfToken) {
          console.error('CSRF token not found');
          return; // Handle the absence of the CSRF token appropriately
        }
      }
      const res = await fetch('/apidet/login/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
        },
        credentials: 'same-origin',
        body: JSON.stringify({ username, password }),
      });
      if (!res.ok) throw new Error('Login failed');
      const data = await res.json();
      setIsAuthenticated(true);
      setUsername(username);
      setError('');

      // Fetch session data after login to update first_name
      await getSession();

    } catch (err) {
      console.error(err);
      setError('Wrong username or password');
    }
  };

  const logout = async () => {
    try {
      const res = await fetch('/apidet/logout', { credentials: 'same-origin' });
      if (!res.ok) throw new Error('Logout failed');
      localStorage.removeItem("selectedGroup"); // Clear the stored group
      setIsAuthenticated(false);
      setUsername('');
      setFirstName('');
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        username,
        first_name,
        last_name,
        email,
        organization, // ✅
        organization_id,
        ps_id,
        plan, // ✅
        organization_permissions: organizationPermissions, // ✅
        isAuthenticated,
        coverage_area,
        department,
        is_staff,
        error,
        getPermissions,
        getDashboardPermissions,
        login,
        logout,
        whoami,
      }}
    >

      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};